"use client"

import { Fragment } from "react"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"

import type { CrossCatalogMatch } from "@/lib/search/cross-catalog"

/** How many tests to list inline before collapsing the rest into "+N more". */
const MAX_TESTS = 3

type CrossCatalogSuggestionProps = {
  type: "biomarkers" | "diseases"
  /** The top suggestable match (caller passes matches[0]). */
  match: CrossCatalogMatch
  /** Localized href to the item's own detail page. Biomarkers only; omit for
   *  diseases (no detail page) — the name renders unlinked and the "+N more"
   *  affordance is hidden. */
  itemHref?: string
  /** Build a localized href to a test page from its slug. */
  testHref: (slug: string) => string
  /** Localized name of the scope the search came up empty in (the active
   *  package filter, or the test/panel on an explorer surface). Shown as a
   *  "No results in {scope}" lead-in so the suggestion has context. */
  scopeLabel?: string
  /** Analytics: which link was clicked. */
  onSelect: (target: "test" | "detail") => void
  /** When the empty scope is a clearable filter (an active package on the
   *  biomarkers / what-we-detect pages), pass a handler to drop it and keep the
   *  query. Renders a "search all packages" CTA so the user can broaden in one
   *  tap instead of leaving the page. Omit on surfaces with no clearable scope
   *  (the explorer's test/panel scope) — the CTA is then hidden. */
  onClearScope?: () => void
}

export function CrossCatalogSuggestion({
  type,
  match,
  itemHref,
  testHref,
  scopeLabel,
  onSelect,
  onClearScope,
}: CrossCatalogSuggestionProps) {
  const t = useTranslations("ui")
  const wt = useTranslations("whatWeDetect")
  const nt = useTranslations("nav")
  const testLabel = (test: CrossCatalogMatch["tests"][number]) =>
    test.navKey ? nt(test.navKey as never) : wt(test.translationKey as never)
  const shown = match.tests.slice(0, MAX_TESTS)
  const extra = match.tests.length - shown.length

  const testList = (
    <>
      {shown.map((test, i) => (
        <Fragment key={test.slug}>
          {i > 0 && ", "}
          <Link
            href={testHref(test.slug)}
            onClick={() => onSelect("test")}
            className="text-aubergine-700 hover:text-aubergine-900 font-medium underline"
          >
            {testLabel(test)}
          </Link>
        </Fragment>
      ))}
      {extra > 0 && itemHref && (
        <>
          {" "}
          <Link
            href={itemHref}
            onClick={() => onSelect("detail")}
            className="text-aubergine-600 hover:text-aubergine-900 underline"
          >
            {t("crossCatalogMore", { count: String(extra) })}
          </Link>
        </>
      )}
    </>
  )

  return (
    <div
      data-testid="cross-catalog-suggestion"
      data-type={type}
      className="mx-auto max-w-xl py-16 text-center"
    >
      {scopeLabel && (
        <p className="type-body-md mb-2 text-neutral-400">
          {t("crossCatalogLead", { scope: scopeLabel })}
        </p>
      )}
      {match.packageQuery ? (
        // The query names the add-on itself ("omega"): point at the add-on,
        // not at whichever of its markers ranked first.
        <p className="type-title-lg text-neutral-700">
          <Link
            href={testHref(match.tests[0].slug)}
            onClick={() => onSelect("test")}
            className="text-aubergine-700 hover:text-aubergine-900 font-medium underline"
          >
            {wt("searchAddonHint", {
              name: match.packageQuery,
              addon: testLabel(match.tests[0]),
            })}
          </Link>
        </p>
      ) : (
        <p className="type-title-lg text-neutral-700">
          {t.rich("crossCatalogPrompt", {
            name: match.item.name,
            bio: (chunks) =>
              itemHref ? (
                <Link
                  href={itemHref}
                  onClick={() => onSelect("detail")}
                  className="font-medium text-neutral-900 underline"
                >
                  {chunks}
                </Link>
              ) : (
                <span className="font-medium text-neutral-900">{chunks}</span>
              ),
            tests: () => testList,
          })}
        </p>
      )}
      {onClearScope && (
        <button
          type="button"
          onClick={onClearScope}
          className="text-aubergine-600 hover:text-aubergine-900 mt-4 cursor-pointer text-sm font-medium underline"
        >
          {t("crossCatalogClearScope")}
        </button>
      )}
    </div>
  )
}
